// Smart Safiri dashboard colour system (smart-safiri-dashboard-colour.md).
// Single source for every colour in the console: emitted as CSS variables by
// the Mantine cssVariablesResolver (theme.ts) and read directly in JS where
// CSS variables can't reach (Google Maps markers/polygons).
//
// DARK THEME DEVIATION (product decision, 2026-09-25): the spec's dark
// neutrals (#141718 canvas / #1E2223 card / #3B4243 border) read flat and grey
// on screen, so dark surfaces, borders and neutral text are re-stepped deeper
// with hairline borders. Brand oxblood, status, fleet, map and chart tokens are
// unchanged. Neutral text still clears AA on the new card (#131617):
// primary 15.6:1, secondary 7.9:1, tertiary 5.3:1; border-interactive 3.1:1.
//
// Rules worth remembering (see the spec for the rest):
//  - oxblood is the ONE primary action per view, never decoration;
//  - destructive (#D92D20) is never the brand red;
//  - warning-solid takes text-primary ink, never white;
//  - every status ships with an icon/label too, never colour alone.

export const RAMPS = {
  oxblood: {
    50: "#FDF4F3",
    100: "#FAE5E4",
    200: "#F3C9C7",
    300: "#E5A09D",
    400: "#CF6B67",
    500: "#A93733",
    600: "#821D1B",
    700: "#6B1715",
    800: "#551210",
    900: "#3A0C0B",
  },
  neutral: {
    0: "#FFFFFF",
    25: "#FAFBFB",
    50: "#F4F6F6",
    100: "#EAEAEA",
    200: "#DDE0E0",
    300: "#C3C8C9",
    400: "#9AA1A2",
    500: "#767E7F",
    600: "#626A6B",
    650: "#545B5C",
    700: "#3B4243",
    750: "#2E3435",
    800: "#2A2F30",
    850: "#262B2C",
    900: "#1E2223",
    950: "#141718",
    975: "#0F1112",
  },
  steel: { 200: "#C2DFE3", 400: "#6FB3BA", 600: "#2F6F76", 700: "#245C62" },
} as const;

type Scheme = "light" | "dark";
type TokenValue = { light: string; dark: string } | string;

const T = (light: string, dark: string = light) => ({ light, dark });

export const TOKENS = {
  // Surfaces
  "surface-canvas": T("#F4F6F6", "#0C0E0F"),
  "surface-default": T("#FFFFFF", "#131617"),
  "surface-raised": T("#FFFFFF", "#1A1E1F"),
  "surface-sunken": T("#EAEAEA", "#0A0B0C"),
  "surface-overlay": T("#FFFFFF", "#1E2224"),
  "surface-inverse": T("#2A2F30", "#EAEAEA"),
  "surface-scrim": T("rgba(30,34,35,0.55)", "rgba(10,12,12,0.72)"),
  "table-header-bg": T("#FAFBFB", "#131617"),

  // Text
  "text-primary": T("#1E2223", "#ECEEEF"),
  "text-secondary": T("#545B5C", "#A4ACAD"),
  "text-tertiary": T("#626A6B", "#848C8E"),
  "text-disabled": T("#9AA1A2", "#4F5658"),
  "text-inverse": T("#FFFFFF", "#141718"),
  "text-brand": T("#821D1B", "#E5A09D"),
  "text-on-brand": T("#FFFFFF"),

  // Borders
  "border-subtle": T("#E4E7E7", "#1C2021"),
  "border-default": T("#D5D9D9", "#232829"),
  "border-strong": T("#C3C8C9", "#343A3C"),
  "border-interactive": T("#767E7F", "#5E6668"),
  "border-focus": T("#821D1B", "#CF6B67"),

  // Actions
  "action-primary-bg": T("#821D1B", "#A93733"),
  "action-primary-bg-hover": T("#6B1715", "#C15450"),
  "action-primary-bg-active": T("#551210", "#8E2C29"),
  "action-secondary-bg": T("#FFFFFF", "#181B1C"),
  "action-secondary-bg-hover": T("#F4F6F6", "#202425"),
  "action-ghost-bg-hover": T("#EAEAEA", "#1B1F20"),
  "action-destructive-bg": T("#D92D20"),
  "action-destructive-bg-hover": T("#B42318", "#F04438"),

  // Brand subtle
  "brand-subtle-bg": T("#FAE5E4", "#24151A"),
  "brand-subtle-fg": T("#6B1715", "#E5A09D"),
  "brand-subtle-border": T("#F3C9C7", "#5A2B29"),

  // Driver / fleet accent
  "accent-driver-fg": T("#2F6F76", "#6FB3BA"),
  "accent-driver-bg": T("#E3F0F2", "#14292C"),
  "accent-driver-solid": T("#2F6F76"),

  // Status
  "success-fg": T("#067647", "#47CD89"),
  "success-bg": T("#ECFDF3", "#0B2B1D"),
  "success-border": T("#ABEFC6", "#1C5138"),
  "success-solid": T("#079455", "#17B26A"),
  "warning-fg": T("#8A5300", "#FDB022"),
  "warning-bg": T("#FFFAEB", "#2E2005"),
  "warning-border": T("#FEDF89", "#5C4109"),
  "warning-solid": T("#F79009"),
  "error-fg": T("#B42318", "#F97066"),
  "error-bg": T("#FEF3F2", "#2E1311"),
  "error-border": T("#FECDCA", "#612723"),
  "error-solid": T("#D92D20", "#F04438"),
  "info-fg": T("#175CD3", "#84ADFF"),
  "info-bg": T("#EFF8FF", "#101C33"),
  "info-border": T("#B2DDFF", "#2A3F66"),
  "info-solid": T("#2E90FA"),
  "state-offline-fg": T("#545B5C", "#AEB5B6"),
  "state-offline-bg": T("#F4F6F6", "#1C2021"),
  "state-offline-border": T("#D5D9D9", "#2A2F31"),

  // Map
  "map-route-active": T("#821D1B", "#CF6B67"),
  "map-route-alt": T("#9AA1A2", "#626A6B"),
  "map-driver-marker": T("#2A2F30", "#EAEAEA"),
  "map-pickup-pin": T("#067647", "#47CD89"),
  "map-dropoff-pin": T("#821D1B", "#A93733"),
  "map-geofence-fill": T("rgba(130,29,27,0.12)", "rgba(207,107,103,0.16)"),

  // Charts — categorical (fixed order, never cycle)
  "viz-cat-1": T("#9E2A26", "#D4736E"),
  "viz-cat-2": T("#1A6FA8", "#4E94CC"),
  "viz-cat-3": T("#B4780F", "#B8871F"),
  "viz-cat-4": T("#7A4E94", "#9E71BD"),
  "viz-cat-5": T("#3F7D3A", "#4F9C4C"),
  "viz-cat-6": T("#B03A6E", "#C96A96"),
  // Charts — sequential (oxblood, re-stepped per theme)
  "viz-seq-1": T("#F3C9C7", "#551210"),
  "viz-seq-2": T("#E5A09D", "#821D1B"),
  "viz-seq-3": T("#CF6B67", "#A93733"),
  "viz-seq-4": T("#A93733", "#CF6B67"),
  "viz-seq-5": T("#821D1B", "#E5A09D"),
  // Charts — diverging
  "viz-div-neg-2": T("#821D1B", "#E5A09D"),
  "viz-div-neg-1": T("#CF6B67", "#C15450"),
  "viz-div-mid": T("#DDE0E0", "#3B4243"),
  "viz-div-pos-1": T("#6FA3C8", "#4E94CC"),
  "viz-div-pos-2": T("#175A86", "#84ADFF"),
  // Charts — anatomy
  "viz-grid": T("#E4E7E7", "#1C2021"),
  "viz-axis": T("#C3C8C9", "#2A2F31"),

  // Elevation (light separates raised surfaces by shadow, dark by a lighter step)
  "shadow-raised": T("0 4px 12px rgba(30,34,35,0.10)", "0 8px 24px rgba(0,0,0,0.55)"),
} satisfies Record<string, TokenValue>;

export type TokenName = keyof typeof TOKENS;

/** `var(--token)` for use in style props, Mantine `c`/`bg`, and charts. */
export const v = (name: TokenName) => `var(--${name})`;

/** Resolved hex/rgba for one scheme — for code that can't use CSS vars (maps). */
export function tokensFor(scheme: Scheme): Record<TokenName, string> {
  const out = {} as Record<TokenName, string>;
  for (const [k, val] of Object.entries(TOKENS)) {
    out[k as TokenName] = typeof val === "string" ? val : val[scheme];
  }
  return out;
}

/** Sequential step (1–5) for an intensity in [0, 1]; 0 → no fill. */
export function seqStep(intensity: number): TokenName | null {
  if (intensity <= 0) return null;
  const step = Math.min(5, Math.max(1, Math.ceil(intensity * 5)));
  return `viz-seq-${step}` as TokenName;
}
